/**
 * Morning Howl API (services/agent/app/howl_routes.py): the daily stand-up board.
 * DWA users only. Every write returns the new board. KB morning-howl.md.
 */
import { authService, authFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export interface HowlMember {
  id: number;
  name: string;
  /** Work email, matched to Google Calendar guests for the who's-out check. */
  email?: string | null;
}

export interface HowlTurn {
  member_id: number;
  position: number;
  /** random = the start button, picked = a click, lupa = the Meet listener (roadmap). */
  source: 'random' | 'picked' | 'lupa';
  called_at: string;
}

export interface HowlStayAfter {
  member_id: number;
  with_member_ids: number[];
  note: string | null;
  source: 'manual' | 'lupa';
  added_at: string;
}

export interface HowlBoard {
  date: string;
  members: HowlMember[];
  turns: HowlTurn[];
  current_member_id: number | null;
  out_member_ids: number[];
  /** Last Google Calendar check: status is 'ok…' or a reason it couldn't run. */
  calendar?: { checked_at: string | null; status: string | null };
  waiting_member_ids: number[];
  stay_after: HowlStayAfter[];
  started: boolean;
  finished: boolean;
  started_at: string | null;
}

export class HowlApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<HowlBoard> {
  const token = authService.getAccessToken();
  const resp = await authFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const payload = await resp.json();
      if (typeof payload.detail === 'string') detail = payload.detail;
    } catch {
      /* ignore */
    }
    throw new HowlApiError(detail, resp.status);
  }
  return resp.json() as Promise<HowlBoard>;
}

export const fetchHowl = () => request('GET', '/howl/today');
export const startHowl = () => request('POST', '/howl/today/start');
export const pickNext = (memberId: number) => request('POST', '/howl/today/next', { member_id: memberId });
export const undoTurn = () => request('POST', '/howl/today/undo');
export const uncheckTurn = (memberId: number) => request('DELETE', `/howl/today/turns/${memberId}`);
export const setOut = (memberId: number, out: boolean) => request('PUT', `/howl/today/out/${memberId}`, { out });
export const setStayAfter = (memberId: number, withMemberIds: number[], note: string | null) =>
  request('PUT', `/howl/today/stay-after/${memberId}`, { with_member_ids: withMemberIds, note });
export const clearStayAfter = (memberId: number) => request('DELETE', `/howl/today/stay-after/${memberId}`);
export const resetHowl = () => request('POST', '/howl/today/reset');
export const checkCalendar = () => request('POST', '/howl/today/calendar');
export const saveRoster = (names: string[]) => request('PUT', '/howl/members', { names });
